import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import { auth } from '@/config/firebase';
import { Terminal as TerminalIcon, Loader2, Unplug, AlertTriangle } from 'lucide-react';

interface RemoteShellPanelProps {
  deviceId: string;
  deviceName: string;
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export const RemoteShellPanel: React.FC<RemoteShellPanelProps> = ({ deviceId, deviceName }) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Session timer
  useEffect(() => {
    if (status === 'connected') {
      const start = Date.now();
      timerRef.current = setInterval(() => {
        setSessionDuration(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setSessionDuration(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  // Handle terminal resize
  useEffect(() => {
    const handleResize = () => {
      if (fitAddonRef.current && xtermRef.current) {
        fitAddonRef.current.fit();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const connect = useCallback(async () => {
    if (status === 'connecting' || status === 'connected') return;

    setStatus('connecting');
    setError(null);

    try {
      // Create shell session via REST API
      const { sessionId } = await edgeDeviceService.createShellSession(deviceId);
      sessionIdRef.current = sessionId;

      // Get Firebase auth token for WebSocket auth
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      // Initialize xterm.js
      if (!terminalRef.current) throw new Error('Terminal container not ready');

      const term = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", Menlo, Monaco, "Courier New", monospace',
        theme: {
          background: '#0a0a0f',
          foreground: '#e4e4e7',
          cursor: '#60a5fa',
          selectionBackground: '#3b82f640',
          black: '#18181b',
          red: '#ef4444',
          green: '#22c55e',
          yellow: '#eab308',
          blue: '#3b82f6',
          magenta: '#a855f7',
          cyan: '#06b6d4',
          white: '#e4e4e7',
          brightBlack: '#52525b',
          brightRed: '#f87171',
          brightGreen: '#4ade80',
          brightYellow: '#facc15',
          brightBlue: '#60a5fa',
          brightMagenta: '#c084fc',
          brightCyan: '#22d3ee',
          brightWhite: '#fafafa',
        },
        scrollback: 5000,
        allowProposedApi: true,
      });

      const fitAddon = new FitAddon();
      const webLinksAddon = new WebLinksAddon();
      term.loadAddon(fitAddon);
      term.loadAddon(webLinksAddon);

      term.open(terminalRef.current);
      fitAddon.fit();

      xtermRef.current = term;
      fitAddonRef.current = fitAddon;

      // Connect WebSocket to shell session
      const wsUrl = edgeDeviceService.getShellWebSocketUrl(sessionId, token);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Shell WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          switch (message.type) {
            case 'connected':
              // WebSocket connected, waiting for shell to start
              term.write('\x1b[1;34mConnecting to edge device...\x1b[0m\r\n');
              break;

            case 'shell_session_status':
              if (message.status === 'started') {
                setStatus('connected');
              } else if (message.status === 'error') {
                setStatus('error');
                setError(message.error);
                term.write(`\x1b[1;31mError: ${message.error}\x1b[0m\r\n`);
              }
              break;

            case 'shell_output':
              term.write(message.data);
              break;

            case 'shell_session_end':
              term.write(`\r\n\x1b[1;33m--- Session ended: ${message.reason || 'disconnected'} ---\x1b[0m\r\n`);
              setStatus('disconnected');
              break;
          }
        } catch (e) {
          console.error('Error processing shell message:', e);
        }
      };

      ws.onclose = () => {
        console.log('Shell WebSocket closed');
        setStatus('disconnected');
      };

      ws.onerror = (e) => {
        console.error('Shell WebSocket error:', e);
        setStatus('error');
        setError('WebSocket connection failed');
      };

      // Send keystrokes to server
      term.onData((data) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'shell_input',
            sessionId,
            data,
          }));
        }
      });

      // Handle terminal resize
      term.onResize(({ rows, cols }) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'shell_resize',
            sessionId,
            rows,
            cols,
          }));
        }
      });

    } catch (err) {
      console.error('Failed to connect shell:', err);
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Connection failed');
    }
  }, [deviceId, status]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN && sessionIdRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'shell_session_end',
          sessionId: sessionIdRef.current,
        }));
      }
      wsRef.current.close();
      wsRef.current = null;
    }

    if (xtermRef.current) {
      xtermRef.current.dispose();
      xtermRef.current = null;
    }

    fitAddonRef.current = null;
    sessionIdRef.current = null;
    setStatus('disconnected');
    setError(null);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <TerminalIcon className="w-4 h-4" />
          Terminal Session
        </h3>
        <div className="flex items-center gap-3">
          {status === 'connected' && (
            <span className="text-xs text-muted-foreground font-mono">
              {formatDuration(sessionDuration)} / 30:00
            </span>
          )}
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${
              status === 'connected' ? 'bg-emerald-500' :
              status === 'connecting' ? 'bg-amber-500 animate-pulse' :
              status === 'error' ? 'bg-red-500' :
              'bg-zinc-500'
            }`} />
            <span className="text-xs text-muted-foreground capitalize">{status}</span>
          </div>
          {status === 'disconnected' || status === 'error' ? (
            <button
              onClick={connect}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors"
            >
              <TerminalIcon className="w-3.5 h-3.5" />
              Connect
            </button>
          ) : status === 'connecting' ? (
            <button disabled className="flex items-center gap-2 px-3 py-1.5 text-sm bg-card border border-border rounded text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Connecting...
            </button>
          ) : (
            <button
              onClick={disconnect}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600/10 text-red-500 border border-red-500/30 rounded hover:bg-red-600/20 transition-colors"
            >
              <Unplug className="w-3.5 h-3.5" />
              Disconnect
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Terminal container */}
      <div
        className={`border border-border rounded-lg overflow-hidden ${
          status === 'disconnected' && !error ? 'bg-muted' : 'bg-[#0a0a0f]'
        }`}
        style={{ minHeight: '400px' }}
      >
        {status === 'disconnected' && !error ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-muted-foreground">
            <TerminalIcon className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">Click "Connect" to open a shell session on <strong>{deviceName}</strong></p>
            <p className="text-xs mt-1 opacity-60">Sessions are limited to 30 minutes. All commands are logged.</p>
          </div>
        ) : (
          <div ref={terminalRef} style={{ height: '400px', width: '100%' }} />
        )}
      </div>

      {status === 'connected' && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/5 border border-amber-500/20 rounded text-xs text-amber-400/80">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          All commands are logged for audit. Session expires in {formatDuration(1800 - sessionDuration)}.
        </div>
      )}
    </div>
  );
};
